import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import ReactHighcharts from 'react-highcharts';
import highchartsMore from 'highcharts/highcharts-more';
import { each, sumBy } from 'lodash';
import accounting from 'accounting';
import tinycolor from 'tinycolor2';
import pluralize from 'pluralize';

import MoneyDisplay, { format as formatMoney } from '../../../../components/money-display';
import { getUserCurrentOrganization } from '../../../Auth/AuthReducer';
import NumberDisplay from '../../../../components/number-display';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';

import styles from './styles.css';

if (typeof window !== 'undefined') {
  highchartsMore(ReactHighcharts.Highcharts);
}
class Chart extends Component {
  constructor() {
    super();
    this.state = {};
  }

  renderGraph(data) {
    const { title, organization, idx } = this.props;
    let suffix = '';
    let symbol = '';
    if (organization) {
      suffix = organization.currency_suffix;
      symbol = organization.currency_symbol;
    }
    const bgColor = tinycolor('rgba(0, 0, 0, 0.65)');

    const config = {
      chart: {
        plotBackgroundColor: null,
        plotBorderWidth: null,
        plotShadow: false,
        backgroundColor: 'rgba(0, 0, 0, 0)',
        type: 'pie',
      },
      credits: {
        enabled: false,
      },
      title: {
        text: null,
      },
      tooltip: {
        formatter: function format() {
          const percentage = accounting.formatNumber(this.percentage, 0);
          const k = this.key;
          const amount = formatMoney(this.point.amount, true, 1, symbol, suffix);
          const totalAmount = formatMoney(this.point.totalAmount, true, 1, symbol, suffix);
          const count = accounting.formatNumber(this.point.count, 0);
          const salesReps = accounting.formatNumber(this.point.totalOwners, 0);
          const deal = pluralize('deal', count);
          const salesRepsText = pluralize('sales rep', this.point.totalOwners);
          return `<span>
            <p style="font-size: 1.2em; font-weight: bold;">${k}</p>
            <div>${count} ${deal} owned by ${salesReps} ${salesRepsText}.</div>
            <div>${amount} of ${totalAmount}.</div>
            <div>${percentage}% of total.</div>
            </span>`;
        },
        useHTML: true,
        backgroundColor: bgColor.toRgbString(),
        borderColor: 'rgba(0, 0, 0, 0.25)',
        style: {
          color: 'rgba(255, 255, 255, 0.9)',
        },
      },
      plotOptions: {
        pie: {
          allowPointSelect: true,
          cursor: 'pointer',
          shadow: false,
          borderWidth: 0,
          size: '70%',
          dataLabels: {
            enabled: true,
            distance: -30,
            format: '<b>{point.name}</b><br>{point.percentage:.1f} %',
            style: {
              color: '#fff',
            },
            filter: {
              property: 'percentage',
              operator: '>',
              value: 0,
            },
          },
        },
      },
      series: [{
        name: title,
        colorByPoint: true,
        data,
      }],
    };
    return (
      <div className={`${styles.graphWrapper} ${styles[`chart-${idx}`]} animated zoomIn`}>
        <ReactHighcharts config={config} ref="chart" />
      </div>
    );
  }

  render() {
    const {
      title, stats, organization, idx,
    } = this.props;
    let suffix = '';
    let symbol = '';
    if (organization) {
      suffix = organization.currency_suffix;
      symbol = organization.currency_symbol;
    }
    const data = [];
    const totalCount = sumBy(stats, 'count');
    const totalAmount = sumBy(stats, 'amount');
    const totalAvg = totalCount > 0 ? totalAmount / totalCount : 0;
    each(stats, (stat) => {
      data.push({
        name: stat.label,
        y: stat.amount,
        percentage: stat.totalAmount > 0 ? stat.amount / stat.totalAmount : 0,
        ...stat,
      });
    });

    const popover = (<Tooltip id="avg-popover">
      Average opportunity size
    </Tooltip>);
    return (
      <section>
        <div className={`${styles.legend} ${styles[`chart-${(idx + 1)}`]} animated fadeIn`}>
          <table className="table table-bordered table-striped table-condensed">
            <thead>
              <tr>
                <th>{title}</th>
                <th className="text-right">Count</th>
                <th className="text-right">Total</th>
                <th className="text-right">
                  <OverlayTrigger trigger={['hover', 'focus']} placement="top" overlay={popover}>
                    <span>Avg Size</span>
                  </OverlayTrigger>
                </th>
                <th className="text-right">%</th>
              </tr>
            </thead>
            <tbody>
              {data.map((d, i) => {
                if (d.count === 0) {
                  return null;
                }

                let subLabelText = null;
                const statKey = d.statKey;
                if (statKey === 'sizes' || statKey === 'cycles') {
                  const { labelAmount, labelOp } = d;
                  let v = statKey === 'sizes' ?
                    formatMoney(labelAmount, true, 1, symbol, suffix) :
                    accounting.formatNumber(labelAmount, 0);
                  if (statKey === 'cycles') {
                    v += ` ${pluralize('day', labelAmount)}`;
                  }
                  subLabelText = `${labelOp}${v}`;
                }
                const labelText = d.legend || d.label;
                return (
                  <tr key={i}>
                    <td>
                      <span> {labelText}</span>
                      {subLabelText !== null && <span className="text-muted"> <nobr>({subLabelText})</nobr></span>}
                    </td>
                    <td className="text-right">
                      <NumberDisplay value={d.count} precision={0} />
                    </td>
                    <td className="text-right">
                      <nobr><MoneyDisplay abbreviated value={d.amount} precision={1} /></nobr>
                    </td>
                    <td className="text-right">
                      <nobr><MoneyDisplay abbreviated value={d.average} precision={1} /></nobr>
                    </td>
                    <td className="text-right">
                      <nobr><NumberDisplay value={d.percentage * 100} precision={1} suffix="%" /></nobr>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan="4" style={{ height: 1, padding: 0, background: '#ccc' }} />
              </tr>
              <tr>
                <th>Total</th>
                <td className="text-right">
                  <NumberDisplay value={totalCount} precision={0} />
                </td>
                <td className="text-right">
                  <nobr><MoneyDisplay abbreviated value={totalAmount} precision={1} /></nobr>
                </td>
                <td className="text-right">
                  <nobr><MoneyDisplay abbreviated value={totalAvg} precision={1} /></nobr>
                </td>
                <td className="text-right">
                  <nobr><NumberDisplay value={100} precision={1} suffix="%" /></nobr>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </section>
    );
  }
}


function mapStateToProps(state) {
  return {
    organization: getUserCurrentOrganization(state),
  };
}

Chart.propTypes = {
  title: PropTypes.string.isRequired,
  stats: PropTypes.array.isRequired,
  idx: PropTypes.number.isRequired,
  organization: PropTypes.object.isRequired,
};
Chart.defaultProps = {
  idx: 0,
};


Chart.contextTypes = {

};
export default connect(mapStateToProps)(Chart);
